/**
 * 类型安全的翻译函数
 *
 * 提供类型安全的翻译功能，替代直接使用字符串的$t函数
 *
 * 使用方式：
 * import { $st } from '@/i18n/safe-translate'
 * import { I18N_KEYS } from '@/i18n/keys'
 *
 * // 在Vue组件中
 * $st(I18N_KEYS.COMMON.SUBMIT)
 *
 * // 在非Vue文件中
 * import { st } from '@/i18n/safe-translate'
 * st(I18N_KEYS.COMMON.SUBMIT)
 *
 * @module i18n/safe-translate
 */

import i18n from './index'
import { isValidTranslationKey, getAllTranslationKeys } from './keys'

/**
 * 开发环境下的翻译key验证
 *
 * 注意：这个函数只验证 key 是否在 I18N_KEYS 中定义
 * 不会验证翻译是否真的存在于语言文件中（那是 vue-i18n 的职责）
 */
function validateTranslationKey(key) {
  if (process.env.NODE_ENV === 'development') {
    // 只在 key 真的无效时才警告
    // 避免与 vue-i18n 的警告混淆
    if (!isValidTranslationKey(key)) {
      console.warn(`[i18n-safe-translate] 警告: 翻译key "${key}" 未在 I18N_KEYS 中定义`);
      console.warn('[i18n-safe-translate] 建议: 使用 I18N_KEYS 常量以获得类型安全和自动补全');

      // 尝试找到相似的key
      const similarKeys = getAllTranslationKeys().filter(validKey =>
        validKey.toLowerCase().includes(key.toLowerCase()) ||
        key.toLowerCase().includes(validKey.toLowerCase())
      );

      if (similarKeys.length > 0) {
        console.warn('[i18n-safe-translate] 您是否想要使用以下key之一？', similarKeys.slice(0, 5));
      }
    }
  }
}

/**
 * 类型安全的翻译函数（用于Vue组件）
 * @param {string} key - 翻译key，应该使用I18N_KEYS中的常量
 * @param {object} values - 翻译参数
 * @returns {string} 翻译后的文本
 */
export function $st(key, values) {
  validateTranslationKey(key);
  const res = i18n.t(key, values);
  return typeof res === 'string' ? res : String(res);
}

/**
 * 类型安全的翻译函数（用于非Vue文件）
 * @param {string} key - 翻译key，应该使用I18N_KEYS中的常量
 * @param {object} values - 翻译参数
 * @returns {string} 翻译后的文本
 */
export function st(key, values) {
  validateTranslationKey(key);
  const res = i18n.t(key, values);
  return typeof res === 'string' ? res : String(res);
}

/**
 * 检查翻译key是否存在
 * @param {string} key - 翻译key
 * @param {string} locale - 语言代码（可选）
 * @returns {boolean} 是否存在
 */
export function hasTranslation(key, locale = i18n.locale) {
  validateTranslationKey(key);
  return i18n.te(key, locale);
}

/**
 * 批量翻译函数
 * @param {string[]} keys - 翻译key数组
 * @param {object} values - 翻译参数
 * @returns {string[]} 翻译后的文本数组
 */
export function stBatch(keys, values) {
  return keys.map(key => st(key, values));
}

/**
 * 条件翻译函数
 * @param {boolean} condition - 条件
 * @param {string} trueKey - 条件为真时的翻译key
 * @param {string} falseKey - 条件为假时的翻译key
 * @param {object} values - 翻译参数
 * @returns {string} 翻译后的文本
 */
export function stConditional(condition, trueKey, falseKey, values) {
  const key = condition ? trueKey : falseKey;
  return st(key, values);
}

/**
 * 创建Vue mixin，提供$st方法
 */
export const safeTranslateMixin = {
  methods: {
    /**
     * Vue组件中的类型安全翻译方法
     */
    $st(key, values) {
      return $st(key, values);
    },

    /**
     * 检查翻译是否存在
     */
    $hasTranslation(key, locale) {
      return hasTranslation(key, locale);
    },

    /**
     * 批量翻译
     */
    $stBatch(keys, values) {
      return stBatch(keys, values);
    },

    /**
     * 条件翻译
     */
    $stConditional(condition, trueKey, falseKey, values) {
      return stConditional(condition, trueKey, falseKey, values);
    }
  }
};

/**
 * 翻译key常量的快捷导出
 */
export { I18N_KEYS } from './keys';
